/**
 * Locale + Region context for SUNTRONIX ONE.
 *
 * Persists the user's language and region preference (localStorage) and exposes
 * helpers to format numbers, currency and dates in that locale. Purely
 * client-side today; the backend can later read the preference from the user
 * profile to translate emails and PDFs.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export const LANGUAGES = [
  { code: "en",    label: "English",             native: "English",   flag: "🇬🇧" },
  { code: "en-US", label: "English (US)",        native: "English",   flag: "🇺🇸" },
  { code: "hi",    label: "Hindi",               native: "हिन्दी",     flag: "🇮🇳" },
  { code: "fr",    label: "French",              native: "Français",  flag: "🇫🇷" },
  { code: "de",    label: "German",              native: "Deutsch",   flag: "🇩🇪" },
  { code: "es",    label: "Spanish",             native: "Español",   flag: "🇪🇸" },
  { code: "pt-BR", label: "Portuguese (Brazil)", native: "Português", flag: "🇧🇷" },
  { code: "ja",    label: "Japanese",            native: "日本語",     flag: "🇯🇵" },
  { code: "zh",    label: "Chinese (Simplified)",native: "简体中文",   flag: "🇨🇳" },
  { code: "ar",    label: "Arabic",              native: "العربية",   flag: "🇸🇦", rtl: true },
];

export const REGIONS = [
  { code: "GLOBAL", label: "Global",     tz: "UTC",              currency: "USD" },
  { code: "IN",     label: "India",      tz: "Asia/Kolkata",     currency: "INR" },
  { code: "UK",     label: "United Kingdom", tz: "Europe/London", currency: "GBP" },
  { code: "EU",     label: "Europe",     tz: "Europe/Berlin",    currency: "EUR" },
  { code: "US",     label: "United States", tz: "America/New_York", currency: "USD" },
  { code: "APAC",   label: "Asia Pacific", tz: "Asia/Singapore", currency: "USD" },
  { code: "MENA",   label: "Middle East & Africa", tz: "Asia/Dubai", currency: "AED" },
  { code: "LATAM",  label: "Latin America", tz: "America/Sao_Paulo", currency: "BRL" },
];

const LS_LANG = "st.locale.language";
const LS_REGION = "st.locale.region";

const LocaleCtx = createContext(null);

export function LocaleProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    try { return window.localStorage.getItem(LS_LANG) || detectBrowserLanguage(); }
    catch { return "en"; }
  });
  const [region, setRegionState] = useState(() => {
    try { return window.localStorage.getItem(LS_REGION) || detectBrowserRegion(); }
    catch { return "GLOBAL"; }
  });

  const setLanguage = useCallback((code) => {
    setLanguageState(code);
    try { window.localStorage.setItem(LS_LANG, code); } catch { /* ignore */ }
  }, []);
  const setRegion = useCallback((code) => {
    setRegionState(code);
    try { window.localStorage.setItem(LS_REGION, code); } catch { /* ignore */ }
  }, []);

  // Reflect direction and lang on <html>
  useEffect(() => {
    const l = LANGUAGES.find((x) => x.code === language);
    document.documentElement.setAttribute("lang", language);
    document.documentElement.setAttribute("dir", l?.rtl ? "rtl" : "ltr");
  }, [language]);

  const value = useMemo(() => {
    const langMeta = LANGUAGES.find((l) => l.code === language) || LANGUAGES[0];
    const regionMeta = REGIONS.find((r) => r.code === region) || REGIONS[0];
    const intlLocale = language;
    return {
      language, setLanguage, region, setRegion,
      languageLabel: langMeta.label, languageNative: langMeta.native, languageFlag: langMeta.flag,
      regionLabel: regionMeta.label, regionTz: regionMeta.tz, currency: regionMeta.currency,
      formatCurrency: (n) => new Intl.NumberFormat(intlLocale, { style: "currency", currency: regionMeta.currency, maximumFractionDigits: 0 }).format(Number(n || 0)),
      formatNumber:   (n) => new Intl.NumberFormat(intlLocale).format(Number(n || 0)),
      formatDate:     (d) => new Intl.DateTimeFormat(intlLocale, { dateStyle: "medium", timeZone: regionMeta.tz }).format(new Date(d)),
      formatDateTime: (d) => new Intl.DateTimeFormat(intlLocale, { dateStyle: "medium", timeStyle: "short", timeZone: regionMeta.tz }).format(new Date(d)),
    };
  }, [language, region, setLanguage, setRegion]);

  return <LocaleCtx.Provider value={value}>{children}</LocaleCtx.Provider>;
}

export function useLocale() {
  const v = useContext(LocaleCtx);
  if (!v) throw new Error("useLocale must be used within LocaleProvider");
  return v;
}

function detectBrowserLanguage() {
  try {
    const raw = (navigator.language || "en").toLowerCase();
    const match = LANGUAGES.find((l) => l.code.toLowerCase() === raw || l.code.toLowerCase().startsWith(raw.split("-")[0]));
    return match?.code || "en";
  } catch { return "en"; }
}
function detectBrowserRegion() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz?.startsWith("Asia/Kolkata")) return "IN";
    if (tz?.startsWith("Europe/London")) return "UK";
    if (tz?.startsWith("Europe/")) return "EU";
    if (tz?.startsWith("America/")) return "US";
    if (tz?.startsWith("Asia/")) return "APAC";
    if (tz?.startsWith("Africa/") || tz?.startsWith("Asia/Dubai")) return "MENA";
  } catch { /* ignore */ }
  return "GLOBAL";
}
